import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Ban, Loader2, Maximize2, Minus, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePoppedConversations } from "@/contexts/PoppedConversationsContext";
import { toast } from "sonner";
import InviteToBookButton from "@/components/messages/InviteToBookButton";
import { useBlockedConversation } from "@/hooks/useBlockedConversation";

type Msg = {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string | null;
  created_at: string;
  deleted_at: string | null;
};

type Props = {
  id: string;
  partnerName: string;
  partnerAvatar: string | null;
  minimized: boolean;
};

export const PoppedConversationWindow = ({ id, partnerName, partnerAvatar, minimized }: Props) => {
  const { user } = useAuth();
  const { close, toggleMinimized } = usePoppedConversations();
  const location = useLocation();
  const onMessagesPage = location.pathname.startsWith("/messages");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { blocked: convBlocked } = useBlockedConversation(id, user?.id);

  // Hide while user is on the Messages page viewing this same convo (avoid duplicate UI)
  const hideForActive = onMessagesPage && new URLSearchParams(location.search).get("c") === id;

  useEffect(() => {
    if (!user || hideForActive) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("messages")
        .select("id, conversation_id, sender_id, content, created_at, deleted_at")
        .eq("conversation_id", id)
        .order("created_at", { ascending: true });
      if (cancelled) return;
      setMsgs((data as Msg[]) || []);
      setLoading(false);
      // mark read
      await supabase
        .from("conversation_participants")
        .update({ last_read_at: new Date().toISOString() })
        .eq("conversation_id", id)
        .eq("user_id", user.id);
    })();

    const channel = supabase
      .channel(`popped-conv-${id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${id}` },
        (payload) => {
          setMsgs((prev) => (prev.some((m) => m.id === (payload.new as Msg).id) ? prev : [...prev, payload.new as Msg]));
          if (user) {
            supabase
              .from("conversation_participants")
              .update({ last_read_at: new Date().toISOString() })
              .eq("conversation_id", id)
              .eq("user_id", user.id)
              .then(() => {});
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages", filter: `conversation_id=eq.${id}` },
        (payload) => {
          setMsgs((prev) => prev.map((m) => (m.id === (payload.new as Msg).id ? (payload.new as Msg) : m)));
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "messages", filter: `conversation_id=eq.${id}` },
        (payload) => {
          setMsgs((prev) => prev.filter((m) => m.id !== (payload.old as Msg).id));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [id, user, hideForActive]);

  useEffect(() => {
    if (minimized) return;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    });
  }, [msgs.length, minimized]);

  const send = async () => {
    if (!user) return;
    if (convBlocked) {
      toast.error("You've blocked this customer", {
        description: "Unblock them to resume messaging.",
      });
      return;
    }
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      const { error } = await supabase.from("messages").insert({
        conversation_id: id,
        sender_id: user.id,
        content: text,
      });
      if (error) throw error;
      setDraft("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  if (hideForActive) return null;

  const initials = (partnerName || "U").slice(0, 2).toUpperCase();

  return (
    <div
      className={cn(
        "w-72 rounded-t-lg border border-border bg-card shadow-2xl flex flex-col overflow-hidden pointer-events-auto",
        minimized ? "h-10" : "h-96",
      )}
    >
      <div className="flex items-center gap-2 px-2.5 h-10 bg-primary text-primary-foreground shrink-0">
        <button
          type="button"
          onClick={() => toggleMinimized(id)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          aria-label={minimized ? "Expand chat" : "Minimize chat"}
        >
          <Avatar className="w-6 h-6 shrink-0">
            {partnerAvatar && <AvatarImage src={partnerAvatar} />}
            <AvatarFallback className="bg-primary-foreground/20 text-primary-foreground text-[10px] font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate">{partnerName}</span>
        </button>
        <Link
          to={`/messages?c=${id}`}
          className="p-1 rounded hover:bg-primary-foreground/15"
          title="Open in Messages"
          aria-label="Open in full Messages page"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </Link>
        <button
          type="button"
          onClick={() => toggleMinimized(id)}
          className="p-1 rounded hover:bg-primary-foreground/15"
          title={minimized ? "Expand" : "Minimize"}
          aria-label={minimized ? "Expand chat" : "Minimize chat"}
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => close(id)}
          className="p-1 rounded hover:bg-primary-foreground/15"
          title="Close"
          aria-label="Close chat"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {!minimized && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-2.5 space-y-1.5 bg-background">
            {loading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : msgs.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-6">No messages yet - say hello!</p>
            ) : (
              msgs.map((m) => {
                const mine = m.sender_id === user?.id;
                const isDeleted = !!m.deleted_at;
                return (
                  <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "rounded-2xl px-3 py-1.5 text-xs max-w-[80%] break-words",
                        isDeleted
                          ? "bg-muted/50 text-muted-foreground italic border border-dashed"
                          : mine
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm",
                      )}
                    >
                      {isDeleted ? "Message deleted" : (m.content || <span className="opacity-60">[attachment]</span>)}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {convBlocked && (
            <div className="px-2 pt-2 flex items-start gap-1.5 text-[11px] text-destructive">
              <Ban className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Customer is blocked. Messaging disabled.</span>
            </div>
          )}
          <div className="p-2 border-t bg-card flex items-end gap-1.5 shrink-0">
            {user && (
              <InviteToBookButton userId={user.id} conversationId={id} compact />
            )}
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={convBlocked ? "Messaging disabled" : "Type a message…"}
              rows={1}
              className="min-h-[36px] max-h-24 resize-none text-xs py-1.5"
              disabled={convBlocked}
            />
            <Button
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={send}
              disabled={sending || convBlocked || !draft.trim()}
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
